import { Flex, Input, Select, TableProps, Tag, DatePicker, Tabs, App, notification, Typography } from "antd";
import { FC, useEffect, useRef, useState, useMemo, useContext } from "react";
import React from "react";
import { useTranslation } from 'react-i18next';
import ExportExcelButton from "@/components/ExportExcelButton";
import ExportProgressModal from "@/components/ExportProgressModal";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import dayjs, { Dayjs } from "dayjs";

import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { dateFormat } from "@/utils/format";
import { orderListV2 } from "@/apis/inventoryv2";
import { OrderV2 } from "@/types/inventoryV2";
import { ChangeEnum, ConfirmedEnum } from "@/types/inventory";
import { User } from "@/types/user";
import OrderDetailV2 from "../OrderDetailV2";
import { CustomTabPaneContext } from "@/components/TabPane";
import IconFolder from "@/assets/images/fileIcon/folder.svg";
import SText from "@/styles/SText";
import WMSAvatar from "@/styles/WMSAvatar";
import useSourceType from "@/hooks/useSourceType";
import useDataList from "@/hooks/useDataList";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

const { RangePicker } = DatePicker;

enum TabEnum {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

interface WarehouseOrderFilters {
  keyword: string;
  orderType: ChangeEnum | undefined;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  confirmed: ConfirmedEnum | undefined;
}

const INITIAL_WAREHOUSE_ORDER_FILTERS: WarehouseOrderFilters = {
  keyword: '',
  orderType: undefined,
  startTime: null,
  endTime: null,
  confirmed: undefined,
};

const EXPORT_INBOUND_ORDER_TYPES: ChangeEnum[] = [
  ChangeEnum.ORDER_IN,
  ChangeEnum.TEMP_IN,
  ChangeEnum.PRE_IN,
  ChangeEnum.IN,
];

const EXPORT_OUTBOUND_ORDER_TYPES: ChangeEnum[] = [
  ChangeEnum.TEMP_OUT,
  ChangeEnum.PRE_OUT,
  ChangeEnum.OUT,
  ChangeEnum.SALES_OUT,
];

const OrderView: FC = () => {
  const { t } = useTranslation();
  const { onOpen } = useContext(CustomTabPaneContext);
  const { changeTypes, getChangeType, getOpType } = useSourceType();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const curOrg = useSelector((state: RootState) => state.orgReducer.curOrg);
  const curUser: User = useSelector((state: RootState) => state.userReducer.curUser);

  const [openDetailId, setOpenDetailId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<TabEnum>(TabEnum.INBOUND);
  const [isMounted, setIsMounted] = useState(false);
  const [list, total, listLoading, { fetchList, rmItem, addItem, updateItem }] = useDataList<
    OrderV2,
    Parameters<typeof orderListV2>
  >({ fetchApi: orderListV2 });
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<WarehouseOrderFilters>(INITIAL_WAREHOUSE_ORDER_FILTERS);
  const { keyword, orderType, startTime, endTime, confirmed } = draftFilters;


  // ✅ 组件卸载时取消请求
  useEffect(() => {
    const controller = abortControllerRef.current;
    return () => {
      if (controller) {
        controller.abort();
      }
    };
  }, []);

  // 组件挂载后设置标志
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // 当组件挂载或依赖项变化时，重新获取数据
  useEffect(() => {
    if (isMounted && curOrg) {
      const nextFilters = replaceFilters(INITIAL_WAREHOUSE_ORDER_FILTERS);
      tableRef?.current?.resetTablePage();
      fetch(0, nextFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isMounted, replaceFilters]);

  const handleExportAll = async (): Promise<OrderV2[]> => {
    try {
      setExportLoading(true);
      setExportProgress(0);
      const allOrderData: OrderV2[] = [];
      const batchSize = 100;
      let offset = 0;
      let hasMore = true;

      while (hasMore && curOrg) {
        let types: number[];
        if (appliedFilters.orderType !== undefined) {
          types = [appliedFilters.orderType];
        } else {
          types = activeTab === TabEnum.INBOUND
            ? EXPORT_INBOUND_ORDER_TYPES
            : EXPORT_OUTBOUND_ORDER_TYPES;
        }

        const response = await orderListV2({
          orgId: curOrg.id,
          keyword: appliedFilters.keyword,
          params: {
            body: appliedFilters.keyword,
            ints: types,
            pagination: { offset, take: batchSize },
            startTime: appliedFilters.startTime ? appliedFilters.startTime.startOf('day').valueOf() : undefined,
            endTime: appliedFilters.endTime ? appliedFilters.endTime.endOf('day').valueOf() : undefined,
          },
          confirmed: appliedFilters.confirmed
        });

        if (response.body && response.body.length > 0) {
          allOrderData.push(...response.body);
          offset += batchSize;
          const progress = Math.min(
            Math.round(
              (allOrderData.length /
                (response.pagination?.size || allOrderData.length)) *
              100
            ),
            99
          );
          setExportProgress(progress);

          if (response.body.length < batchSize) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }

      setExportProgress(100);
      return allOrderData;
    } catch (error) {
      notification.error({
        placement: 'top',
        message: t("common.error"),
        description: t("common.exportErr")
      });
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  const orderColumns = useMemo(() => [
    { key: 'number', label: activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber') },
    { key: 'creator', label: t('order.preparedBy') },
    { key: 'createdAt', label: t('order.preparationTime') },
    { key: 'preTime', label: t('warehouse.requiredArrivalAt') },
    { key: 'arrivalAt', label: t('warehouse.actualArrivalAt') },
    { key: 'confirmedAt', label: t('order.confirmedTime') },
    { key: 'type', label: t('order.oprtType') },
    { key: 'opType', label: t('order.operationSource') },
    { key: 'confirmState', label: t('order.operationStatus') },
  ], [activeTab, t]);

  const orderDataMapper = (item: OrderV2, t: any) => {
    const typeInfo = getChangeType(item.type);
    const opTypeInfo = getOpType(item.opType);

    // 根据tab和确认状态显示不同的状态文字
    let statusLabel = '';
    if (activeTab === TabEnum.INBOUND) {
      statusLabel = item.confirmState === 0 ? t('order.inTransit') : t('order.inboundCompleted');
    } else {
      statusLabel = item.confirmState === 0 ? t('order.preparing') : t('order.outboundCompleted');
    }

    const numberLabel = activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber');

    return {
      [numberLabel]: item.number || '',
      [t('order.preparedBy')]: getUserName(item.creator) || '',
      [t('order.preparationTime')]: item.createdAt ? dateFormat(dayjs(item.createdAt)) : '',
      [t('warehouse.requiredArrivalAt')]: item.preTime ? dateFormat(dayjs(item.preTime)) : '',
      [t('warehouse.actualArrivalAt')]: item.arrivalAt ? dateFormat(dayjs(item.arrivalAt)) : '',
      [t('order.confirmedTime')]: item.confirmedAt ? dateFormat(dayjs(item.confirmedAt)) : '',
      [t('order.oprtType')]: typeInfo?.label || '',
      [t('order.operationSource')]: opTypeInfo?.label || '',
      [t('order.operationStatus')]: statusLabel,
    };
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key as TabEnum);
  };

  const tableTabs = [
    {
      key: TabEnum.INBOUND,
      label: t('warehouse.inbound'),
    },
    {
      key: TabEnum.OUTBOUND,
      label: t('warehouse.outbound'),
    },
  ];

  // 根据 tab 筛选操作类型选项
  const filteredChangeTypes = useMemo(() => {
    if (activeTab === TabEnum.INBOUND) {
      // 入库类型
      return changeTypes.filter(item =>
        [ChangeEnum.ORDER_IN, ChangeEnum.PRE_IN, ChangeEnum.IN].includes(item.value)
      );
    } else {
      // 出库类型
      return changeTypes.filter(item =>
        [ChangeEnum.PRE_OUT, ChangeEnum.OUT, ChangeEnum.SALES_OUT].includes(item.value)
      );
    }
  }, [activeTab]);

  // 根据 tab 动态生成表格列
  const columns = useMemo((): TableProps<OrderV2>["columns"] => [
    {
      title: activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber'),
      dataIndex: "number",
      key: "number",
      width: 200,
      ellipsis: true,
      sorter: (a, b) => {
        const valA = a?.number ?? "";
        const valB = b?.number ?? "";
        return valA.localeCompare(valB);
      },
      render(value, record, index) {
        return <Typography.Link onClick={() => setOpenDetailId(record.id)}>{record.number}</Typography.Link>
      },
    },
    {
      title: t('order.preparedBy'),
      dataIndex: "creator",
      key: "creator",
      width: 140,
      render: (_, record) => (
        record.creator && <Flex align="center">
          <WMSAvatar size={30} creator={record.creator} uniChar={record.creator.id} avatarId={record.creator.avatar} alt={getUserAvatarTip(record.creator)} />
          <SText text={getUserName(record.creator)} style={{ width: 70 }} />
        </Flex>
      ),
    },
    {
      title: t('order.preparationTime'),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 100,
      render: (createdAt) => {
        const fullTime = dayjs(createdAt).format('YYYY-MM-DD HH:mm:ss');
        return React.createElement('span', { title: fullTime }, dateFormat(dayjs(createdAt)));
      },
    },
    {
      title: t('warehouse.requiredArrivalAt'),
      dataIndex: "preTime",
      key: "preTime",
      width: 100,
      render: (preTime) => preTime && dateFormat(dayjs(preTime)),
    },
    {
      title: t('warehouse.actualArrivalAt'),
      dataIndex: "arrivalAt",
      key: "arrivalAt",
      width: 100,
      render: (arrivalAt) => arrivalAt && dateFormat(dayjs(arrivalAt)),
    },
    {
      title: t('order.confirmedTime'),
      dataIndex: "confirmedAt",
      key: "confirmedAt",
      width: 100,
      render: (confirmedAt) => confirmedAt && dateFormat(dayjs(confirmedAt)),
    },
    {
      title: t('order.remark'),
      dataIndex: "desc",
      key: "desc",
      width: 100,
      ellipsis: true,
    },
    {
      title: t('order.oprtType'),
      key: "type",
      dataIndex: "type",
      width: 120,
      render: (_, { type, id }) => {
        let info = getChangeType(type);
        return (
          <SText text={info?.label ?? ""} tagMode color={info?.color} />
        );
      },
    },
    {
      title: t('order.operationSource'),
      key: "opType",
      dataIndex: "opType",
      width: 90,
      render: (_, { opType, id }) => {
        let info = getOpType(opType);
        return (
          <SText text={info?.label ?? ""} tagMode color={info?.color} />
        );
      },
    },
    {
      title: t('order.operationStatus'),
      key: "confirmState",
      dataIndex: "confirmState",
      width: 90,
      render: (_, { confirmState, id }) => {
        // 根据tab显示不同的状态文字
        if (activeTab === TabEnum.INBOUND) {
          // 入库：0-在途，1-已入库
          const label = confirmState === 0 ? t('order.inTransit') : t('order.inboundCompleted');
          const color = confirmState === 0 ? 'orange' : 'green';
          return <SText text={label ?? ""} tagMode color={color} />
        } else {
          // 出库：0-备料，1-已出库
          const label = confirmState === 0 ? t('order.preparing') : t('order.outboundCompleted');
          const color = confirmState === 0 ? 'orange' : 'green';
          return <SText text={label ?? ""} tagMode color={color} />
        }
      },
    },
    {
      title: t("common.attachment"),
      key: "docId",
      fixed: 'right',
      width: 100,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={IconFolder}
            alt=""
            onClick={() => onOpen("doc", item)}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
  ], [activeTab, t]);

  const headerChildren = (
    <Flex vertical style={{ width: "100%" }}>
      {/* 第一行：Tab 和筛选条件 */}
      <Flex justify="space-between" style={{ marginBottom: 10 }} align="center">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tableTabs}
          type="card"
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <ListFilterBar
            onSearch={() => handleSearch()}
            onReset={() => handleClearFilters()}
            disabled={listLoading}
          >
            {({ submit }) => (
              <>
                <RangePicker
                  value={[startTime, endTime]}
                  onChange={value => updateDraftFilters({
                    startTime: value?.[0] ?? null,
                    endTime: value?.[1] ?? null,
                  })}
                  placeholder={[t('order.startTime'), t('order.endTime')]}
                  style={{ width: 280 }}
                />
                <Select style={{ minWidth: 160 }} value={confirmed} placeholder={t('warehouse.plconfed')}
                  options={[
                    { label: t('warehouse.noconf'), value: ConfirmedEnum.NOTCONFIRM },
                    { label: t('warehouse.confed'), value: ConfirmedEnum.CONFIRM },
                  ]}
                  onChange={value => updateDraftFilters({ confirmed: value })} allowClear
                />
                <Select
                  allowClear
                  placeholder={t('order.selectChangeTypePl')}
                  value={orderType}
                  onChange={value => updateDraftFilters({ orderType: value })}
                  options={filteredChangeTypes}
                  style={{ width: 160 }}
                />
                <Input
                  style={{ width: 200 }}
                  value={keyword}
                  onChange={event => updateDraftFilters({ keyword: event.target.value })}
                  onPressEnter={submit}
                  placeholder={t('order.searchByOrderNumber')}
                />
              </>
            )}
          </ListFilterBar>
        </div>
      </Flex>
      {/* 第二行：导出按钮 */}
      <Flex gap={10}>
        <ExportExcelButton
          list={list}
          columns={orderColumns}
          defaultSelectedColumns={[
            activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber'),
            t('order.preparedBy'),
            t('order.preparationTime')
          ]}
          dataMapper={orderDataMapper}
          fileName={`${t('warehouse.orderList')}.xlsx`}
        />
        <ExportExcelButton
          isAll
          loadData={handleExportAll}
          columns={orderColumns}
          defaultSelectedColumns={[
            activeTab === TabEnum.INBOUND ? t('warehouse.deliveryOrderNumber') : t('warehouse.materialRequisitionNumber'),
            t('order.preparedBy'),
            t('order.preparationTime')
          ]}
          dataMapper={orderDataMapper}
          fileName={`${t('warehouse.orderList')}_全部.xlsx`}
        />
      </Flex>
    </Flex>
  );

  const fetch = (offset: number, filters: WarehouseOrderFilters) => {
    if (!curOrg) {
      console.warn('curOrg is not available');
      return;
    }

    // ✅ 取消上一次请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // ✅ 创建新的 AbortController
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;

    // 根据 tab 确定要查询的类型列表
    // 如果用户选择了特定类型，则只查询该类型；否则查询该 tab 下的所有类型
    let types: number[];
    if (filters.orderType !== undefined) {
      types = [filters.orderType];
    } else {
      types = activeTab === TabEnum.INBOUND
        ? [ChangeEnum.PRE_IN, ChangeEnum.IN]
        : [ChangeEnum.PRE_OUT, ChangeEnum.OUT];
    }
    // 使用 V2 API，通过 ints 参数传递多个类型
    fetchList({
      orgId: curOrg.id,
      keyword: filters.keyword,
      params: {
        body: filters.keyword,
        ints: types,  // 传递多个类型
        pagination: { offset, take },
        startTime: filters.startTime ? filters.startTime.startOf('day').valueOf() : undefined,
        endTime: filters.endTime ? filters.endTime.endOf('day').valueOf() : undefined,
      },
      confirmed: filters.confirmed,
      controller, // ✅ 传递 controller 用于取消请求
    })
      .catch((err: any) => {
        // ✅ 忽略取消的请求
        if (err?.name === 'AbortError') {
          return;
        }
        console.error('Fetch error:', err);
      });
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_WAREHOUSE_ORDER_FILTERS);
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  return (
    <Flex vertical style={{ width: '100%', height: '100%' }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => fetch(offset, appliedFilters)}
        extHeight={25}
      />
      {openDetailId && <OrderDetailV2
        id={openDetailId}
        open={true}
        handleRm={rmItem}
        handleCancel={(order, type) => {
          if (order) {
            if (type === "ADD") addItem({ ...order, creator: curUser });
            if (type === "DEL") rmItem(order.id);
            if (type === "UPDATE") updateItem(order)
          }
          setOpenDetailId("")
        }}
      />}
      <ExportProgressModal visible={exportLoading} progress={exportProgress} />
    </Flex>
  );
};

export default OrderView;
